import React from 'react';
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import SectionSurface from '../../shared/SectionSurface';
import { PBI_READY_TO_BUILD } from '../../../../content/enterpriseMessaging';

const PILLAR_ICONS = [MagnifyingGlassIcon, ChatBubbleLeftRightIcon, BellAlertIcon];
const PILLAR_TONES = [
  'bg-brand-50 text-brand-600 ring-brand-100',
  'bg-[rgba(0,18,63,0.08)] text-brand-900 ring-[rgba(0,18,63,0.16)]',
  'bg-accent-50 text-accent-700 ring-accent-100',
];

export default function ReadyToBuild() {
  const { eyebrow, title, subtitle, ctas, pillars } = PBI_READY_TO_BUILD;

  return (
    <SectionSurface
      as="section"
      variant="muted"
      decor="flow"
      connectTo="brand"
      bleed="top"
      flowTop
      overlap
      className="scroll-mt-24 section-into-footer"
    >
      <div className="container-x section">
        <div className="relative overflow-hidden rounded-3xl border border-brand-100/70 bg-gradient-to-br from-brand-50/80 via-white/90 to-accent-50/70 px-6 py-14 shadow-md sm:px-12">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-0 overflow-x-clip overflow-y-visible"
          >
            <div className="absolute -top-20 -left-12 h-64 w-64 rounded-full bg-gradient-to-br from-brand-200/40 to-transparent blur-3xl" />
            <div className="absolute -bottom-24 -right-12 h-72 w-72 rounded-full bg-gradient-to-br from-accent-200/40 to-transparent blur-3xl" />
            <div className="absolute inset-0 bg-ring-accents opacity-60" />
          </div>

          <div className="relative z-[1] mx-auto max-w-3xl text-center">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">{subtitle}</p>

            <div className="mt-9 flex flex-wrap justify-center gap-3">
              {ctas.map((cta) => (
                <a
                  key={cta.label}
                  href={cta.href}
                  className={cta.variant === 'primary' ? 'btn-primary btn-lg' : 'btn-secondary btn-lg'}
                >
                  {cta.label}
                  {cta.variant === 'primary' && <ArrowRightIcon className="h-4 w-4" />}
                </a>
              ))}
            </div>
          </div>

          {pillars && pillars.length > 0 && (
            <div className="relative z-[1] mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
              {pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[i] || MagnifyingGlassIcon;
                return (
                  <div
                    key={p.title}
                    className="rounded-2xl border border-ink-200/80 bg-white/85 p-5 text-left shadow-xs backdrop-blur-sm"
                  >
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-inset ${PILLAR_TONES[i % 3]}`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold text-ink-900">{p.title}</h3>
                    <p className="mt-1.5 text-base leading-relaxed text-ink-600">{p.body}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SectionSurface>
  );
}
